import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Copy, Phone, ShieldCheck, ExternalLink, Bot, RotateCcw, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import PageHeader from "@/components/PageHeader";
import { errMsg, getBotSettings, getWhatsAppStatus, getWhatsAppCredentials, saveWhatsAppCredentials, checkWhatsAppConnection, updateBotPrompt, generateBotPrompt, API_BASE } from "@/lib/api";

const DEMO_MODE = process.env.REACT_APP_DEMO_MODE === "true";
const FIELDS = [
  ["phone_number_id", "Identificador del número (Phone Number ID)", "text"],
  ["access_token", "Token de acceso de Meta", "password"],
  ["app_secret", "Secreto de la aplicación (App Secret)", "password"],
  ["verify_token", "Token de verificación del webhook", "password"],
];
const EMPTY = { phone_number_id: "", access_token: "", app_secret: "", verify_token: "" };
const STEPS = [
  { title: "Prepara tu número en Meta", description: "Abre WhatsApp Manager y registra el número que usará tu negocio con la API de WhatsApp Business." },
  { title: "Guarda los datos de conexión", description: "Completa los cuatro campos de esta página y pulsa «Guardar conexión». El token de verificación lo eliges tú; después usarás el mismo en Meta." },
  { title: "Activa la recepción de mensajes", description: "En la configuración de webhooks de tu aplicación de Meta, pega la URL que aparece abajo y tu token de verificación. Verifica la URL y suscribe el campo «messages»." },
  { title: "Comprueba el número y haz una prueba", description: "Pulsa «Comprobar conexión» para validar los datos del número. Después envía un mensaje desde otro teléfono al WhatsApp de tu negocio y revisa la recepción y la respuesta en «Chats»." },
];
const FIELD_HELP = {
  phone_number_id: "Es el identificador que muestra Meta para tu número, no el número de teléfono.",
  access_token: "Usa el token de tu aplicación de Meta con acceso a WhatsApp Business.",
  app_secret: "Lo encontrarás en la configuración básica de tu aplicación de Meta.",
  verify_token: "Elige un token privado. Debe coincidir exactamente con el que configures en el webhook de Meta.",
};

function Row({ ok, label }) {
  return <li className="flex items-center gap-2 text-sm">{ok ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-slate-400" />}{label}</li>;
}

export default function Settings() {
  const { user } = useOutletContext();
  const canManageBot = user.role === "owner" && !DEMO_MODE;
  const [ideas, setIdeas] = useState("");
  const [draft, setDraft] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState("");
  const [wa, setWa] = useState(null);
  const [bot, setBot] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [saving, setSaving] = useState(false);
  const [credentials, setCredentials] = useState(EMPTY);
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const webhook = new URL(`${API_BASE}/webhook/whatsapp`, window.location.origin).href;
  const canEdit = !DEMO_MODE && wa?.can_manage && wa?.encryption_ready;

  const loadStatus = async () => {
    const [status, access] = await Promise.all([getWhatsAppStatus(), getWhatsAppCredentials()]);
    setWa({ ...status, ...access });
  };
  useEffect(() => {
    loadStatus().catch(() => setError("No se pudo leer la configuración de WhatsApp."));
    getBotSettings().then((b) => { setBot(b); setPrompt(b.prompt); }).catch(() => {});
  }, []);

  const saveCredentials = async (event) => {
    event.preventDefault();
    setSavingCredentials(true);
    setError("");
    try {
      const values = Object.fromEntries(Object.entries(credentials).filter(([, value]) => value.trim()));
      const status = await saveWhatsAppCredentials(values);
      setWa((previous) => ({ ...previous, ...status }));
      setCredentials(EMPTY);
      toast.success("Credenciales guardadas de forma cifrada");
    } catch (e) { setError(errMsg(e, "No se pudo guardar la conexión.")); }
    finally { setSavingCredentials(false); }
  };
  const checkConnection = async () => {
    setChecking(true);
    setError("");
    try {
      const result = await checkWhatsAppConnection();
      setWa((previous) => ({ ...previous, ...result }));
      if (result.phone_error) setError(result.phone_error);
      else if (result.display_phone_number) toast.success("Meta reconoce el número del negocio");
      else setError("Completa la configuración antes de comprobar la conexión.");
    } catch (e) { setError(errMsg(e, "No se pudo comprobar la conexión.")); }
    finally { setChecking(false); }
  };
  const savePrompt = async () => {
    setSaving(true);
    try { await updateBotPrompt(prompt); toast.success("Instrucciones base guardadas"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  const generateInstructions = async () => {
    setGenerating(true);
    setGenerationError("");
    try {
      const result = await generateBotPrompt(ideas);
      setDraft(result.prompt);
    } catch (e) { setGenerationError(errMsg(e, "No se pudieron generar las instrucciones. Inténtalo de nuevo.")); }
    finally { setGenerating(false); }
  };

  return (
    <div className="h-full overflow-y-auto scroll-thin" data-testid="settings-shell">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <PageHeader title="WhatsApp y ajustes" subtitle="Conecta el número de WhatsApp del negocio a esta instalación." />
        {error && <p role="alert" className="mb-5 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p>}
        <div className="grid lg:grid-cols-2 gap-6">
          <section className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5" data-testid="whatsapp-status-card">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl bg-[#25D366] text-white flex items-center justify-center"><Phone className="h-5 w-5" /></div>
              <div><p className="text-xs uppercase tracking-wider font-semibold text-slate-400">Número del negocio</p>
                <p className="font-display text-xl font-extrabold" data-testid="wa-phone-number">{wa?.display_phone_number || (wa ? "Pendiente de conexión" : "Cargando…")}</p>
                {wa?.verified_name && <p className="text-xs text-slate-500">{wa.verified_name}</p>}
              </div>
            </div>
            <ul className="space-y-2">
              <Row ok={wa?.phone_number_id_set} label="Identificador del número" />
              <Row ok={wa?.access_token_set} label="Token de acceso" />
              <Row ok={wa?.app_secret_set} label="Secreto de la aplicación" />
              <Row ok={wa?.verify_token_set} label="Verificación del webhook" />
            </ul>
            {DEMO_MODE ? <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900">Esta copia es una demostración local. La conexión con Meta se configura en la instalación publicada.</p>
              : wa && !wa.can_manage ? <p className="text-sm text-slate-500">Solo el dueño puede cambiar las credenciales del negocio.</p>
              : wa && !wa.encryption_ready ? <p className="text-sm text-amber-800">Falta configurar la clave de cifrado de esta instalación antes de guardar credenciales.</p> : null}
            {wa?.can_manage && (
              <form onSubmit={saveCredentials} className="space-y-4" data-testid="whatsapp-credentials-form">
                {FIELDS.map(([key, label, type]) => (
                  <div key={key}><label htmlFor={`wa-${key}`} className="block text-sm font-semibold mb-1.5">{label}</label>
                    <Input id={`wa-${key}`} name={key} type={type} value={credentials[key]} autoComplete="off" disabled={!canEdit || savingCredentials}
                      aria-describedby={`wa-help-${key}`}
                      placeholder={wa[`${key}_set`] ? "Configurado; deja vacío para conservar" : key === "verify_token" ? "Elige tu token de verificación" : "Introduce el valor de tu cuenta de Meta"}
                      onChange={(event) => setCredentials((previous) => ({ ...previous, [key]: event.target.value }))} />
                    <p id={`wa-help-${key}`} className="mt-1 text-xs text-slate-500">{FIELD_HELP[key]}</p>
                  </div>
                ))}
                <p className="text-xs text-slate-500">Los secretos se guardan cifrados y no vuelven a mostrarse. Los campos vacíos conservan el valor guardado.</p>
                <div className="flex flex-wrap gap-2">
                  <Button type="submit" disabled={!canEdit || savingCredentials || !Object.values(credentials).some((value) => value.trim())} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
                    {savingCredentials && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Guardar conexión
                  </Button>
                  <Button type="button" variant="outline" onClick={checkConnection} disabled={DEMO_MODE || !wa.configured || checking} className="rounded-full">
                    {checking && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Comprobar conexión
                  </Button>
                </div>
              </form>
            )}
            {wa?.can_manage && !wa.configured && <p className="text-xs text-slate-500">«Comprobar conexión» se habilita después de guardar los cuatro datos. Esa comprobación valida el número; la prueba desde otro teléfono confirma que llegan los mensajes.</p>}
          </section>
          <section className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="wa-guide">
            <h3 className="font-display text-lg font-bold flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" />Conectar el número de tu negocio</h3>
            <ol className="mt-5 space-y-5">{STEPS.map((step, i) => <li key={step.title} className="flex gap-3"><span aria-hidden="true" className="h-7 w-7 shrink-0 rounded-full bg-emerald-500 text-white text-sm font-bold flex items-center justify-center">{i + 1}</span><div className="min-w-0"><p className="text-sm font-semibold text-slate-800">{step.title}</p><p className="mt-1 text-sm leading-relaxed text-slate-600">{step.description}</p></div></li>)}</ol>
            <div className="mt-6 border-t border-slate-100 pt-5">
              <p className="text-sm font-semibold mb-2">URL del webhook de esta instalación</p>
              <div className="flex items-center gap-2 rounded-xl bg-slate-50 p-3 border border-slate-200">
                <code className="flex-1 min-w-0 text-xs break-all" data-testid="webhook-url">{webhook}</code>
                <Button type="button" size="icon" variant="ghost" aria-label="Copiar URL del webhook" onClick={async () => { try { await navigator.clipboard.writeText(webhook); toast.success("Copiado"); } catch { toast.error("No se pudo copiar la URL"); } }}><Copy className="h-4 w-4" /></Button>
              </div>
              <p className="mt-2 text-xs text-slate-500">Meta necesita una dirección pública HTTPS. Cada cliente utiliza la URL de su propia instalación.</p>
            </div>
            <a href="https://business.facebook.com/wa/manage/phone-numbers/" target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:underline">Abrir WhatsApp Manager <ExternalLink className="h-3.5 w-3.5" /></a>
          </section>
        </div>
        <section className="mt-6 bg-white rounded-2xl border border-slate-200 p-6" data-testid="bot-base-prompt-card">
          <h3 className="font-display text-lg font-bold flex items-center gap-2"><Bot className="h-5 w-5 text-emerald-600" />Instrucciones base del bot</h3>
          <p className="text-sm text-slate-500 mt-1">La información, preguntas frecuentes y catálogo de este negocio se añaden desde «Entrenar bot».</p>
          {canManageBot && <div className="mt-5 rounded-xl border border-emerald-100 bg-emerald-50/50 p-4 space-y-3">
            <label htmlFor="ai-instruction-ideas" className="block text-sm font-semibold">Crea tus instrucciones con IA</label>
            <p className="text-sm text-slate-600">Usa los datos de «Entrenar bot» y tu catálogo. Añade cómo quieres que atienda a tus clientes.</p>
            <Textarea id="ai-instruction-ideas" data-testid="ai-instruction-ideas" value={ideas} onChange={(e) => setIdeas(e.target.value)} maxLength={1500} rows={3} disabled={generating}
              placeholder="Ejemplo: responde de forma cercana, usa pocos emojis y ofrece un asesor si el cliente tiene dudas." />
            <Button type="button" onClick={generateInstructions} disabled={generating || saving} variant="outline" className="rounded-full border-emerald-300 text-emerald-800">
              {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}{generating ? "Generando instrucciones…" : "Generar instrucciones con IA"}
            </Button>
            {generating && <p role="status" className="text-xs text-slate-500">La IA está preparando el borrador. Puede tardar unos segundos.</p>}
            {generationError && <p role="alert" className="text-sm text-rose-700">{generationError}</p>}
            {draft && <div className="space-y-3 border-t border-emerald-200 pt-4" data-testid="ai-instruction-draft">
              <label htmlFor="ai-draft-text" className="block text-sm font-semibold">Borrador generado · revísalo antes de usarlo</label>
              <Textarea id="ai-draft-text" value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={4000} rows={9} />
              <p className="text-xs text-slate-600">Comprueba que las instrucciones coincidan con tu negocio. Todavía no se ha guardado ningún cambio.</p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" disabled={generating || draft.trim().length < 10} onClick={() => { setPrompt(draft); setDraft(""); toast.success("Borrador aplicado. Pulsa Guardar para activar las instrucciones."); }} className="rounded-full bg-emerald-600 hover:bg-emerald-700">Usar este borrador</Button>
                <Button type="button" variant="ghost" disabled={generating} onClick={() => setDraft("")}>Descartar borrador</Button>
              </div>
            </div>}
          </div>}
          <label htmlFor="system-prompt" className="mt-5 block text-sm font-semibold">Instrucciones del bot</label>
          <Textarea id="system-prompt" data-testid="system-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)} readOnly={!canManageBot} maxLength={4000} rows={7} className="mt-2 font-mono text-xs" />
          <div className="mt-3 flex gap-2">
            <Button data-testid="save-prompt-button" onClick={savePrompt} disabled={!canManageBot || saving || generating || prompt.trim().length < 10} className="rounded-full bg-emerald-600 hover:bg-emerald-700">Guardar</Button>
            <Button variant="outline" data-testid="reset-prompt-button" disabled={!canManageBot || generating} onClick={() => bot && setPrompt(bot.default_prompt)} className="rounded-full"><RotateCcw className="h-4 w-4 mr-1.5" />Restaurar</Button>
          </div>
        </section>
      </div>
    </div>
  );
}
